import { DateRange } from '@mui/x-date-pickers-pro';
import { TZDate } from '@repo/tzdate';
import { RefObject, useCallback } from 'react';
import { createAddProductsToCartEvent } from 'src/state/cart/cart.reducer';
import { cartAtom } from 'src/state/cart/cartAtom';
import { showBasketAtom } from 'src/state/ui/showBasket.atom';
import { toggleBookingAtom } from 'src/state/ui/toggleBooking.atom';
import { TicketOptionWithQuantity, Product, ProductInstance, OctoUnitReference } from '@repo/types';
import { updateQuantityData } from '@repo/widget-utils/price-helper';
import { useConfigurations } from '@repo/widget-utils/widgetsConfiguration';
import {
    getCutoffDateFromStartAndCutoffTime,
    productDisablePaymentPlans,
    productInstancesHasDepositPrices,
} from '@repo/widget-utils/cart/cartUtils';
import { useBookingContext } from 'src/widgets/BookingContext';
import {
    setSelectedProducts,
    setSelectedTimeSlot,
    setSelectedTicketType,
    setQuantities,
    setHasChosenDate,
    setAttemptedBooking,
} from 'src/state/booking/bookingReducer';
import { routeToAppropriateCheckout } from 'src/utils/widget/checkoutRouting';
import { octoCartAtom } from 'src/state/octo-cart/octoCartAtom';
import { initializeOctoCartEvent } from 'src/state/octo-cart/octoCart.reducer';
import { createOctoBooking } from '@repo/widget-utils/services/fetchers/octo-api';
import { useAtom } from 'ximple';

export function useOnClickBook(
    quantities: TicketOptionWithQuantity[],
    product: Product | null,
    setDateRange: React.Dispatch<React.SetStateAction<DateRange<TZDate>>>,
    dontResetProduct?: boolean,
) {
    const {
        selectedProducts,
        selectedTicketType,
        selectedTimeSlot,
        hasChosenDate,
        attemptedBooking,
        bookingDispatch,
        shouldShowBasketOnBook,
        boxRef,
    } = useBookingContext();
    const [octoCart] = useAtom(octoCartAtom);
    const configuration = useConfigurations();

    const onClickBook = useCallback(() => {
        const fn = async () => {
            if (!attemptedBooking) bookingDispatch(setAttemptedBooking(true));

            if (
                !selectedProducts?.length ||
                !hasChosenDate ||
                !quantities.some((q) => q.quantity > 0)
            ) {
                return;
            }

            const selected: ProductInstance[] = [...(selectedProducts as any)];
            if (product?.type === 'accommodation' && !selectedTicketType) return;
            if (product?.type === 'timeslot') {
                if (!selectedTimeSlot?.products) return;
                const [firstTimeslot] = selectedTimeSlot.products;
                selected[0] = {
                    ...selected[0],
                    timeslots: selectedTimeSlot.products,
                    orignalTimeslots: selectedProducts[0].timeslots,
                    start: firstTimeslot.start,
                    end: selectedTimeSlot.products[selectedTimeSlot.products.length - 1].end,
                    cutoffDate: getCutoffDateFromStartAndCutoffTime(
                        firstTimeslot.start,
                        firstTimeslot.cutoffTime,
                    ),
                    cutoffTime: firstTimeslot.cutoffTime,
                } as (typeof selected)[0];
            }

            const hasDepositPrices = productInstancesHasDepositPrices(selectedProducts);
            const disablePaymentPlans = productDisablePaymentPlans(
                product,
                false,
                hasDepositPrices,
                configuration.disableMembershipBooking,
            );
            if (configuration.octo) {
                const [firstProductInstance] = selected;

                const unitItems: OctoUnitReference[] = quantities
                    .filter((q) => q.quantity > 0)
                    .flatMap((q) => Array(q.quantity).fill({ unitId: q.id }));

                const response = await createOctoBooking({
                    productId: product!.id,
                    availabilityId: firstProductInstance.id,
                    optionId: firstProductInstance.productOptionId!,
                    expirationMinutes: 15,
                    notes: 'Booking made via widget',
                    uuid: 'e71f35fe-5eab-4335-b173-a8922540a2be', // TODO: need this?,
                    unitItems: unitItems,
                    orderId: octoCart.id,
                });

                await octoCartAtom.update(initializeOctoCartEvent(response.orderId));
            } else {
                await cartAtom.update(
                    createAddProductsToCartEvent(
                        selected,
                        quantities,
                        disablePaymentPlans,
                        undefined,
                        selectedTicketType,
                    ),
                );
            }

            if (shouldShowBasketOnBook) {
                showBasketAtom.update({
                    visible: true,
                    refocusElementOnClose: boxRef.current
                        ? (boxRef as RefObject<HTMLDivElement | null>)
                        : undefined,
                });
            }

            toggleBookingAtom.update({ visible: false });
            setDateRange([null, null]);

            if (!dontResetProduct) {
                bookingDispatch(setSelectedTimeSlot(undefined));
                bookingDispatch(setSelectedProducts(undefined));
            }

            bookingDispatch(setSelectedTicketType(undefined));
            bookingDispatch(setAttemptedBooking(false));
            bookingDispatch(setHasChosenDate(false));
            bookingDispatch(
                setQuantities(
                    updateQuantityData(selected ?? [], product?.ticketOptions ?? [], quantities),
                ),
            );

            if (!shouldShowBasketOnBook) routeToAppropriateCheckout();
        };
        fn();
    }, [
        attemptedBooking,
        selectedProducts,
        hasChosenDate,
        quantities,
        boxRef,
        product,
        setDateRange,
        selectedTicketType,
        selectedTimeSlot,
        shouldShowBasketOnBook,
        dontResetProduct,
        bookingDispatch,
        configuration.disableMembershipBooking,
        configuration.octo,
        octoCart.id,
    ]);

    return onClickBook;
}
